import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Outlet, useNavigate } from 'react-router';
import { AutoLogoutDialog, Loader } from '@link/share/components';
import { clearAutoLogoutStorage, useAutoLogout } from '@link/share/hooks';
import { useStore } from '@link/supplier/stores';
import supplierAuth from '@link/supplier/vendors/firebase';
import Header from '../Header';
import Sidebar from '../Sidebar';
import styles from './base.module.css';

const BaseLayout: React.FC = () => {
  const { auth, supplier } = useStore();
  const navigate = useNavigate();

  const handleAutoLogout = useCallback(async () => {
    clearAutoLogoutStorage();
    await supplierAuth.logout();
    supplier.clearSelectSupplierInfo();
  }, [supplier]);

  const { warningVisible, remainingSeconds, onContinue } =
    useAutoLogout(handleAutoLogout);

  useEffect(() => {
    if (auth.isLinkAdmin && !supplier.suppliersLoaded) {
      supplier.getSuppliers();
    }
  }, [auth.isLinkAdmin, supplier.suppliersLoaded, supplier]);

  useEffect(() => {
    if (auth.isLinkAdmin && supplier.suppliersLoaded) {
      supplier.initFromSessionStorage();
    }
  }, [auth.isLinkAdmin, supplier.suppliersLoaded, supplier]);

  useEffect(() => {
    if (!auth.loading && auth.isUnauthorizedUser) {
      auth.clearUser();
      navigate('/login', { replace: true });
    }
  }, [auth.isUnauthorizedUser, auth.loading, auth, navigate]);

  useEffect(() => {
    if (!auth.isLinkAdmin && auth.userSupplierId) {
      supplier.setSelectSupplierId(auth.userSupplierId);
      supplier.getDetail(auth.userSupplierId);
    }
  }, [auth.isLinkAdmin, auth.userSupplierId, supplier]);

  return (
    <div className={styles.base}>
      <Header />
      <div className={styles.container}>
        <Sidebar />
        <main className={styles.main}>
          <div className={styles.inner}>
            {supplier.selectSupplierId || auth.userSupplierId ? (
              <Outlet key={supplier.selectSupplierId} />
            ) : (
              <Loader />
            )}
          </div>
        </main>
      </div>
      <AutoLogoutDialog
        visible={warningVisible}
        remainingSeconds={remainingSeconds}
        onContinue={onContinue}
        onLogout={handleAutoLogout}
      />
    </div>
  );
};

const BaseLayoutView = observer(BaseLayout);
export default BaseLayoutView;
